import { isPlatformBrowser } from '@angular/common';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { AuthService, BASE_API_URL, IAuthTokenUser } from '@cnt-workspace/data-access';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

// cnt-landing-local fork of @curbnturf/auth's jwtInterceptor — that shared
// interceptor reads/mutates AuthFacade (the NgRx `auth` slice), which
// cnt-landing no longer uses as its session source of truth (see
// data-access-cnt/auth/auth.service.ts). Same attach-token/renew-on-expiry
// behavior, driven by the local AuthService instead.
const attachAuthHeader = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
): Observable<HttpEvent<unknown>> => {
  return auth.authSession$.pipe(
    first(),
    switchMap((currentUser: IAuthTokenUser | undefined) => {
      if (
        currentUser &&
        (!currentUser.authExpires || currentUser.authExpires < DateTime.now().toMillis() + 30000)
      ) {
        setTimeout(() => auth.triggerRenew());
        return auth.renewResult$.pipe(
          first(),
          switchMap(() => attachAuthHeader(request, next, auth)),
        );
      } else if (currentUser?.authIdToken) {
        return next(
          request.clone({
            setHeaders: {
              Authorization: currentUser.authIdToken,
            },
          }),
        );
      } else {
        return next(request);
      }
    }),
  );
};

export const jwtInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const auth = inject(AuthService);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformBrowser(platformId) && request.url.includes(BASE_API_URL)) {
    return attachAuthHeader(request, next, auth);
  }

  return next(request);
};
